// Visit room — a two-column cockpit. Left: the live call (the doctor's primary action — start/host
// the Zoom meeting, with join-window state + end controls). Right: patient context — the thin
// Patient Brief (REPLACE of the chart), the Huddle note (the only CC-authored note), and Manage
// (D4 reschedule/cancel). "End visit" writes an optional thin ≤280 recap then opens the EMR hand-off.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "@forcebase-ai/api-client";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { endVisit } from "../lib/endVisit";
import { formatDateTime, formatDay, labelText } from "../lib/format";
import { Loading, ErrorState, StatusPill } from "../components/ui";
import { PatientBrief } from "../components/PatientBrief";
import { HuddleNote } from "../components/HuddleNote";
import { SlotGrid } from "../components/SlotGrid";
import type { DoctorAppointment, HostJoinTicket, PatientBrief as Brief } from "@forcebase-ai/schema/doctor";
import "../styles-visit.css";

const VideoIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

type Tab = "brief" | "huddle" | "manage";

// Mirror the server join window (zoom/window.ts OPEN_BEFORE_MS): the room opens 10 min early.
const JOIN_OPENS_BEFORE_MS = 10 * 60 * 1000;

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m ${String(secs).padStart(2, "0")}s`;
  return `${secs}s`;
}

export function VisitScreen() {
  const { id = "" } = useParams();
  const api = useApi();
  const appt = useAsync(() => api.appointment(id), [id]);

  if (appt.loading) return <Loading />;
  if (appt.error) return <ErrorState message={appt.error} />;
  if (!appt.data) return <ErrorState message="Visit not found." />;

  return <VisitInner appt={appt.data} apptId={id} onChanged={appt.reload} />;
}

function VisitInner({ appt, apptId, onChanged }: { appt: DoctorAppointment; apptId: string; onChanged: () => void }) {
  const api = useApi();
  const brief = useAsync(() => api.patientBrief(appt.patient_id), [appt.patient_id]);
  const [tab, setTab] = useState<Tab>("brief");

  const p = brief.data?.patient;
  const name = p ? `${p.first_name} ${p.last_name}` : "Patient";

  return (
    <>
      <div className="dp-head">
        <div>
          <div className="dp-head__greet">
            <Link to="/queue" style={{ color: "var(--red)", textDecoration: "none", fontWeight: 600 }}>
              &larr; Queue
            </Link>
          </div>
          <h1 className="dp-head__title">
            Visit <em>room.</em>
          </h1>
          <div className="vr-subhead">
            {name} · {formatDateTime(appt.scheduled_at)} · {labelText(appt.label)} <StatusPill status={appt.status} />
          </div>
        </div>
        <div className="dp-head__actions">
          <VideoHost appointmentId={apptId} scheduledAt={appt.scheduled_at} onStarted={onChanged} />
          {appt.status === "live" && <EndMeetingBar apptId={apptId} onEnded={onChanged} />}
        </div>
      </div>

      {/* Patient context — full width */}
      <div className="dp-tabs">
        <button className={`dp-tab ${tab === "brief" ? "is-active" : ""}`} onClick={() => setTab("brief")}>
          Patient brief
        </button>
        <button className={`dp-tab ${tab === "huddle" ? "is-active" : ""}`} onClick={() => setTab("huddle")}>
          Huddle note
        </button>
        <button className={`dp-tab ${tab === "manage" ? "is-active" : ""}`} onClick={() => setTab("manage")}>
          Manage
        </button>
      </div>
      <div style={{ marginTop: 16 }}>
        {tab === "brief" && <BriefTab brief={brief.data} loading={brief.loading} error={brief.error} />}
        {tab === "huddle" && (
          <section className="dp-card">
            <HuddleNote appointmentId={apptId} />
          </section>
        )}
        {tab === "manage" && <ManageTab appointmentId={apptId} scheduledAt={appt.scheduled_at} onChanged={onChanged} />}
      </div>

      <div className="vr-recap">
        <EndVisit patientId={appt.patient_id} healthieApptId={appt.healthie_appointment_id as string | undefined} />
      </div>
    </>
  );
}

function VideoHost({
  appointmentId,
  scheduledAt,
  onStarted,
}: {
  appointmentId: string;
  scheduledAt: string;
  onStarted: () => void;
}) {
  const api = useApi();
  const [phase, setPhase] = useState<"idle" | "connecting" | "open" | "waiting" | "error">("idle");
  const [ticket, setTicket] = useState<HostJoinTicket | null>(null);
  const [message, setMessage] = useState("");
  const [canRetry, setCanRetry] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const opensAt = new Date(scheduledAt).getTime() - JOIN_OPENS_BEFORE_MS;

  async function start() {
    setPhase("connecting");
    setCanRetry(false);
    // Open the tab synchronously inside the click gesture so the host start_url isn't popup-blocked
    // after the async fetch. start_url signs the clinician in as the Zoom meeting HOST.
    const tab = window.open("", "_blank");
    try {
      const t = await api.joinVisitHost(appointmentId);
      setTicket(t);
      if (t.startUrl) {
        if (tab) tab.location.href = t.startUrl;
        else window.open(t.startUrl, "_blank", "noopener");
        setPhase("open");
        onStarted(); // status flipped to live server-side → refresh so the End meeting bar appears
      } else {
        tab?.close();
        setMessage("Could not start the visit.");
        setCanRetry(true);
        setPhase("error");
      }
    } catch (e) {
      tab?.close();
      const code = e instanceof ApiError ? e.code : (e as { code?: string }).code;
      if (code === "out_of_window") {
        // Too early — show a live countdown to when the room opens; flip back when it does.
        setNow(Date.now());
        setPhase("waiting");
        return;
      }
      setMessage(
        code === "not_ready"
          ? "The secure room is still being provisioned."
          : e instanceof ApiError
            ? e.message
            : "Could not start the visit.",
      );
      setCanRetry(true);
      setPhase("error");
    }
  }

  // While waiting (too early), tick the countdown; re-enable Start the moment the window opens.
  useEffect(() => {
    if (phase !== "waiting") return;
    const timer = setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (n >= opensAt) {
        clearInterval(timer);
        setPhase("idle");
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [phase, opensAt]);

  return (
    <>
      {phase === "idle" && (
        <button className="dp-btn dp-btn--green" onClick={start}>
          {VideoIcon} Start visit (host)
        </button>
      )}
      {phase === "connecting" && <span className="vr-bar__hint">Opening Zoom…</span>}
      {phase === "waiting" && (
        <span className="vr-bar__hint">
          Opens in <strong>{formatCountdown(opensAt - now)}</strong>
        </span>
      )}
      {phase === "open" && ticket?.startUrl && (
        <a className="dp-btn dp-btn--green" href={ticket.startUrl} target="_blank" rel="noreferrer">
          {VideoIcon} Reopen in Zoom
        </a>
      )}
      {phase === "error" && (
        <>
          <span className="vr-bar__hint">{message}</span>
          {canRetry && (
            <button className="dp-btn dp-btn--green" onClick={start}>
              Try again
            </button>
          )}
        </>
      )}
    </>
  );
}

function EndMeetingBar({ apptId, onEnded }: { apptId: string; onEnded: () => void }) {
  const [busy, setBusy] = useState(false);
  async function end() {
    setBusy(true);
    try {
      await endVisit(apptId);
      onEnded();
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="dp-card">
      <div className="dp-row">
        <button className="dp-btn dp-btn--ghost" disabled={busy} onClick={end}>
          {busy ? "Ending…" : "End meeting"}
        </button>
      </div>
    </section>
  );
}

function BriefTab({ brief, loading, error }: { brief: Brief | null; loading: boolean; error: string | null }) {
  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} />;
  if (!brief) return <ErrorState message="Brief unavailable." />;
  return (
    <section className="dp-card">
      <PatientBrief brief={brief} />
    </section>
  );
}

function ManageTab({
  appointmentId,
  scheduledAt,
  onChanged,
}: {
  appointmentId: string;
  scheduledAt: string;
  onChanged: () => void;
}) {
  const api = useApi();
  const [slot, setSlot] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function reschedule() {
    if (!slot) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.reschedule(appointmentId, { new_scheduled_at: slot, reason: reason.trim() || "Provider rescheduled" });
      onChanged();
      setMsg("Rescheduled.");
      setSlot(null);
    } catch (e) {
      setMsg(
        e instanceof ApiError && e.code === "slot_unavailable"
          ? "That time was just taken."
          : e instanceof ApiError
            ? e.message
            : "Could not reschedule.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!reason.trim()) {
      setMsg("A cancellation reason is required.");
      return;
    }
    setBusy(true);
    try {
      await api.cancel(appointmentId, { reason: reason.trim() });
      onChanged();
      setMsg("Cancelled.");
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : "Could not cancel.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="dp-card">
      <div className="dp-card__title">Reschedule or cancel</div>
      <p className="dp-card__sub">Currently {formatDay(scheduledAt)}. Pick a new time:</p>
      <SlotGrid value={slot} onChange={setSlot} />
      <div className="dp-field" style={{ marginTop: 12 }}>
        <label>Reason</label>
        <input className="dp-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for the change" />
      </div>
      {msg && (
        <p className="muted" style={{ marginTop: 8 }}>
          {msg}
        </p>
      )}
      <div className="dp-row">
        <button className="dp-btn dp-btn--primary" disabled={!slot || busy} onClick={reschedule}>
          {busy ? "Working…" : "Confirm new time"}
        </button>
        <button className="dp-btn dp-btn--ghost" disabled={busy} onClick={cancel}>
          Cancel visit
        </button>
      </div>
    </section>
  );
}

function EndVisit({ patientId, healthieApptId }: { patientId: string; healthieApptId?: string }) {
  const api = useApi();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  async function saveNote() {
    if (!note.trim() || !healthieApptId) return;
    setBusy(true);
    try {
      await api.writeVisitSummary(patientId, {
        last_visit_at: new Date().toISOString(),
        summary_text: note.trim().slice(0, 280),
        healthie_appointment_id: healthieApptId,
      });
      setSaved(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="dp-card">
      <div className="dp-card__title">Visit recap</div>
      <p className="dp-card__sub">
        Optional ≤280-char recap for the member (not part of the record). The full chart lives in the
        EMR — open it from the patient brief.
      </p>
      <textarea
        className="dp-textarea"
        rows={3}
        maxLength={280}
        value={note}
        placeholder="Short recap for the member…"
        onChange={(e) => {
          setNote(e.target.value);
          setSaved(false);
        }}
      />
      <div className="dp-row">
        <button className="dp-btn dp-btn--ghost" disabled={busy || !note.trim() || !healthieApptId} onClick={saveNote}>
          {busy ? "Saving…" : "Save recap"}
        </button>
        {saved && <span className="muted">Saved.</span>}
        {!healthieApptId && <span className="muted">Recap available after the visit is mirrored.</span>}
      </div>
    </section>
  );
}
